import { useEffect, useRef, useState } from "react";
import { Image, Typography } from "antd";

interface PostcardConfig {
  sender_name?: string;
  sender_avatar?: string;
  receiver_name?: string;
  location?: string;
  date?: string;
  stamp_image?: string;
  cover_image?: string;
  message?: string;
  quote?: string;
  video?: string;
  gallery?: string[];
}

interface Props {
  config: PostcardConfig;
}

/* ─── Floating Dust Motes ─── */
const DustMotes = () => {
  const [motes] = useState(() =>
    Array.from({ length: 15 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      top: Math.random() * 100,
      delay: Math.random() * 8,
      duration: 8 + Math.random() * 12,
      size: 2 + Math.random() * 3,
      opacity: 0.15 + Math.random() * 0.25,
    })),
  );

  return (
    <>
      {motes.map((m) => (
        <div
          key={m.id}
          className="fixed pointer-events-none"
          style={{
            left: `${m.left}%`,
            top: `${m.top}%`,
            width: `${m.size}px`,
            height: `${m.size}px`,
            borderRadius: "50%",
            background: "rgba(180,150,100,0.6)",
            opacity: m.opacity,
            animation: `pc-drift ${m.duration}s ease-in-out ${m.delay}s infinite`,
            zIndex: 2,
          }}
        />
      ))}
    </>
  );
};

/* ─── Animated Section (scroll reveal) ─── */
const AnimatedSection = ({
  children,
  className = "",
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setTimeout(() => setVisible(true), delay);
      },
      { threshold: 0.15 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [delay]);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        transition: "opacity 0.9s ease, transform 0.9s ease",
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(30px)",
      }}
    >
      {children}
    </div>
  );
};

/* ─── Airmail Border Strip ─── */
const AirmailStrip = () => (
  <div className="flex w-full" style={{ height: 8 }}>
    {Array.from({ length: 30 }, (_, i) => (
      <div
        key={i}
        className="flex-1"
        style={{
          background: i % 2 === 0 ? "#c0392b" : "#2c5aa0",
          transform: "skewX(-20deg)",
          marginLeft: i === 0 ? 0 : -2,
        }}
      />
    ))}
  </div>
);

/* ─── Wax Seal ─── */
const WaxSeal = ({ size = 56 }: { size?: number }) => (
  <div
    className="relative flex items-center justify-center"
    style={{
      width: size,
      height: size,
      borderRadius: "50%",
      background: "radial-gradient(circle at 35% 35%, #e74c3c, #a93226 60%, #7b241c)",
      boxShadow: "0 4px 15px rgba(167,52,42,0.4), inset 0 2px 4px rgba(255,255,255,0.2)",
      animation: "pc-seal-glow 3s ease-in-out infinite",
    }}
  >
    <span style={{ fontSize: size * 0.4, filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.3))" }}>💌</span>
    {/* Wax drip details */}
    <div
      className="absolute"
      style={{
        bottom: -4,
        left: "50%",
        transform: "translateX(-50%)",
        width: size * 0.3,
        height: 8,
        borderRadius: "0 0 50% 50%",
        background: "radial-gradient(ellipse at top, #c0392b, #a93226)",
      }}
    />
  </div>
);

/* ─── Postage Stamp ─── */
const PostageStamp = ({ image, size = 80 }: { image?: string; size?: number }) => (
  <div
    className="relative"
    style={{
      width: size,
      height: size * 1.2,
      padding: 4,
      background: "#f5f0e8",
      /* Perforated edge simulation */
      filter: "drop-shadow(2px 3px 6px rgba(0,0,0,0.15))",
      borderRadius: 2,
    }}
  >
    {/* Perforation dots */}
    <div className="absolute inset-0" style={{ zIndex: 2 }}>
      {/* Top edge */}
      {Array.from({ length: 8 }, (_, i) => (
        <div
          key={`t${i}`}
          className="absolute rounded-full"
          style={{
            width: 4,
            height: 4,
            background: "#f5f0e8",
            top: -2,
            left: `${(i + 1) * (100 / 9)}%`,
            boxShadow: "0 0 0 1px rgba(0,0,0,0.08)",
          }}
        />
      ))}
      {/* Bottom edge */}
      {Array.from({ length: 8 }, (_, i) => (
        <div
          key={`b${i}`}
          className="absolute rounded-full"
          style={{
            width: 4,
            height: 4,
            background: "#f5f0e8",
            bottom: -2,
            left: `${(i + 1) * (100 / 9)}%`,
            boxShadow: "0 0 0 1px rgba(0,0,0,0.08)",
          }}
        />
      ))}
      {/* Left edge */}
      {Array.from({ length: 10 }, (_, i) => (
        <div
          key={`l${i}`}
          className="absolute rounded-full"
          style={{
            width: 4,
            height: 4,
            background: "#f5f0e8",
            left: -2,
            top: `${(i + 1) * (100 / 11)}%`,
            boxShadow: "0 0 0 1px rgba(0,0,0,0.08)",
          }}
        />
      ))}
      {/* Right edge */}
      {Array.from({ length: 10 }, (_, i) => (
        <div
          key={`r${i}`}
          className="absolute rounded-full"
          style={{
            width: 4,
            height: 4,
            background: "#f5f0e8",
            right: -2,
            top: `${(i + 1) * (100 / 11)}%`,
            boxShadow: "0 0 0 1px rgba(0,0,0,0.08)",
          }}
        />
      ))}
    </div>
    {/* Stamp content */}
    <div
      className="w-full h-full overflow-hidden"
      style={{
        border: "2px solid rgba(0,0,0,0.06)",
      }}
    >
      {image ? (
        <img src={image} alt="stamp" className="w-full h-full object-cover" />
      ) : (
        <div
          className="w-full h-full flex flex-col items-center justify-center"
          style={{ background: "linear-gradient(135deg, #c0392b, #e74c3c)" }}
        >
          <span style={{ fontSize: size * 0.3 }}>✈️</span>
          <span
            style={{
              fontFamily: "'Courier Prime', monospace",
              fontSize: "0.5rem",
              color: "#fff",
              marginTop: 2,
              letterSpacing: "0.05em",
            }}
          >
            AIRMAIL
          </span>
        </div>
      )}
    </div>
    {/* Postmark overlay */}
    <div
      className="absolute flex items-center justify-center"
      style={{
        top: "15%",
        right: "-30%",
        width: size * 0.9,
        height: size * 0.9,
        borderRadius: "50%",
        border: "2px solid rgba(0,0,0,0.12)",
        transform: "rotate(-15deg)",
        opacity: 0.4,
      }}
    >
      <div className="text-center" style={{ lineHeight: 1.1 }}>
        <div style={{ fontFamily: "'Courier Prime', monospace", fontSize: "0.45rem", color: "#333" }}>VIA AIRMAIL</div>
        <div className="my-0.5" style={{ height: 1, background: "rgba(0,0,0,0.15)", width: "100%" }} />
        <div style={{ fontFamily: "'Courier Prime', monospace", fontSize: "0.4rem", color: "#555" }}>2025</div>
      </div>
    </div>
  </div>
);

/* ─── Vintage Divider ─── */
const VintageDivider = () => (
  <div className="flex items-center justify-center gap-3 py-8">
    <div className="h-px w-16" style={{ background: "linear-gradient(to right, transparent, rgba(139,109,63,0.3))" }} />
    <span style={{ color: "rgba(139,109,63,0.5)", fontSize: "0.6rem", letterSpacing: "0.3em" }}>✦ ✦ ✦</span>
    <div className="h-px w-16" style={{ background: "linear-gradient(to left, transparent, rgba(139,109,63,0.3))" }} />
  </div>
);

/* ═══════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════ */
const PublicPostcard = ({ config }: Props) => {
  return (
    <div
      className="min-h-screen relative overflow-x-hidden"
      style={{
        background: "#f5f0e8",
        /* Subtle paper texture via noise */
      }}
    >
      {/* Global Postcard Styles */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Caveat:wght@400;500;600;700&family=Playfair+Display:ital,wght@0,400;0,700;1,400&family=Courier+Prime:ital@0;1&family=Lora:ital,wght@0,400;0,600;1,400&display=swap');

        @keyframes pc-drift {
          0%, 100% { transform: translate(0, 0); }
          25% { transform: translate(15px, -10px); }
          50% { transform: translate(-10px, 15px); }
          75% { transform: translate(10px, 5px); }
        }
        @keyframes pc-float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-6px) rotate(1deg); }
        }
        @keyframes pc-seal-glow {
          0%, 100% { box-shadow: 0 4px 15px rgba(167,52,42,0.3); }
          50% { box-shadow: 0 6px 25px rgba(167,52,42,0.5); }
        }
        @keyframes pc-write {
          0% { width: 0; }
          100% { width: 100%; }
        }
        @keyframes pc-stamp-land {
          0% { transform: rotate(-20deg) scale(1.3); opacity: 0; }
          60% { transform: rotate(3deg) scale(0.95); }
          100% { transform: rotate(-3deg) scale(1); opacity: 1; }
        }
        @keyframes pc-scroll-quill {
          0% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(6px) rotate(5deg); }
          100% { transform: translateY(0) rotate(0deg); }
        }
        @keyframes pc-ink-blot {
          0%, 100% { transform: scale(1); opacity: 0.08; }
          50% { transform: scale(1.15); opacity: 0.12; }
        }

        .pc-paper {
          background: linear-gradient(135deg, #faf6ef 0%, #f0e9dc 50%, #ebe4d4 100%);
          box-shadow:
            0 2px 20px rgba(0,0,0,0.08),
            0 0 0 1px rgba(139,109,63,0.1),
            inset 0 0 80px rgba(139,109,63,0.03);
        }

        .pc-paper-texture::before {
          content: '';
          position: absolute;
          inset: 0;
          background:
            repeating-linear-gradient(
              0deg,
              transparent,
              transparent 28px,
              rgba(139,109,63,0.04) 28px,
              rgba(139,109,63,0.04) 29px
            );
          pointer-events: none;
          z-index: 1;
        }

        .pc-handwriting {
          fontFamily: 'Caveat', cursive;
          color: #2c1810;
        }

        .pc-ink-color { color: #1a365d; }
        .pc-brown { color: #8b6d3f; }

        .pc-cover-overlay {
          background: linear-gradient(180deg,
            rgba(245,240,232,0) 0%,
            rgba(245,240,232,0.4) 50%,
            rgba(245,240,232,0.95) 80%,
            #f5f0e8 100%
          );
        }

        .pc-gallery-img {
          transition: transform 0.4s cubic-bezier(0.25,0.46,0.45,0.94), box-shadow 0.4s ease;
        }
        .pc-gallery-img:hover {
          transform: scale(1.02) rotate(-0.5deg);
          box-shadow: 0 12px 40px rgba(0,0,0,0.12);
        }

        .pc-tape {
          background: linear-gradient(135deg, rgba(255,230,150,0.7), rgba(255,220,120,0.5));
          backdrop-filter: blur(1px);
        }
      `}</style>

      <DustMotes />

      {/* ══════════ HERO — POSTCARD FRONT ══════════ */}
      <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden">
        {/* Cover Image */}
        <div className="absolute inset-0">
          {config.cover_image ? (
            <img src={config.cover_image} alt="" className="w-full h-full object-cover" />
          ) : (
            <div
              className="w-full h-full"
              style={{
                background: "linear-gradient(135deg, #deb887 0%, #d2a679 30%, #c4956a 60%, #b8860b 100%)",
              }}
            />
          )}
          <div className="pc-cover-overlay absolute inset-0" />
          {/* Vignette */}
          <div
            className="absolute inset-0"
            style={{
              background: "radial-gradient(ellipse at center, transparent 50%, rgba(139,109,63,0.15) 100%)",
            }}
          />
        </div>

        {/* Airmail stripe top */}
        <div className="absolute top-0 left-0 w-full z-10">
          <AirmailStrip />
        </div>

        {/* Stamp in corner */}
        <div
          className="absolute top-6 right-4 z-20"
          style={{ animation: "pc-stamp-land 0.8s ease-out forwards", transform: "rotate(-3deg)" }}
        >
          <PostageStamp image={config.stamp_image} size={72} />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 text-center px-8 py-20 max-w-md">
          {/* Location badge */}
          {config.location && (
            <div
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-8"
              style={{
                background: "rgba(255,255,255,0.85)",
                boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                border: "1px solid rgba(139,109,63,0.15)",
              }}
            >
              <span style={{ fontSize: "0.75rem" }}>📍</span>
              <span
                style={{
                  fontFamily: "'Courier Prime', monospace",
                  fontSize: "0.7rem",
                  letterSpacing: "0.15em",
                  color: "#8b6d3f",
                  textTransform: "uppercase",
                }}
              >
                {config.location}
              </span>
            </div>
          )}

          {/* Main greeting */}
          <h1
            style={{
              fontFamily: "'Caveat', cursive",
              fontSize: "clamp(2.8rem, 10vw, 4rem)",
              color: "#2c1810",
              lineHeight: 1.1,
              marginBottom: 12,
              textShadow: "0 2px 8px rgba(0,0,0,0.05)",
            }}
          >
            {config.quote || "Wish You Were Here"}
          </h1>

          {/* Date */}
          {config.date && (
            <p
              style={{
                fontFamily: "'Courier Prime', monospace",
                fontSize: "0.8rem",
                color: "rgba(44,24,16,0.4)",
                letterSpacing: "0.1em",
              }}
            >
              {config.date}
            </p>
          )}

          {/* Scroll indicator */}
          <div className="mt-16 flex flex-col items-center gap-2">
            <span
              style={{
                fontFamily: "'Courier Prime', monospace",
                fontSize: "0.6rem",
                letterSpacing: "0.2em",
                color: "rgba(44,24,16,0.3)",
                textTransform: "uppercase",
              }}
            >
              Lật bưu thiếp
            </span>
            <span style={{ fontSize: "1.2rem", animation: "pc-scroll-quill 2s ease-in-out infinite" }}>🪶</span>
          </div>
        </div>

        {/* Airmail stripe bottom */}
        <div className="absolute bottom-0 left-0 w-full z-10">
          <AirmailStrip />
        </div>
      </section>

      {/* ══════════ POSTCARD BACK — THE MESSAGE ══════════ */}
      <section className="relative py-16 px-4">
        <AnimatedSection>
          <div
            className="pc-paper pc-paper-texture rounded-lg max-w-md mx-auto relative overflow-hidden"
            style={{ padding: "32px 24px" }}
          >
            {/* Tape decoration top-left */}
            <div
              className="pc-tape absolute"
              style={{
                top: -6,
                left: 20,
                width: 60,
                height: 22,
                transform: "rotate(-5deg)",
                borderRadius: 2,
                zIndex: 5,
              }}
            />

            {/* Vertical divider line (classic postcard) */}
            <div
              className="absolute hidden sm:block"
              style={{
                top: 32,
                bottom: 32,
                left: "50%",
                width: 1,
                background: "rgba(139,109,63,0.15)",
                borderLeft: "1px dashed rgba(139,109,63,0.2)",
              }}
            />

            {/* Receiver info — top */}
            {config.receiver_name && (
              <div className="mb-6 relative z-10">
                <span
                  style={{
                    fontFamily: "'Courier Prime', monospace",
                    fontSize: "0.65rem",
                    color: "rgba(139,109,63,0.5)",
                    letterSpacing: "0.15em",
                    textTransform: "uppercase",
                  }}
                >
                  Gửi đến
                </span>
                <h2
                  style={{
                    fontFamily: "'Caveat', cursive",
                    fontSize: "1.8rem",
                    color: "#1a365d",
                    marginTop: 2,
                  }}
                >
                  {config.receiver_name} 💙
                </h2>
                {/* Underline decoration */}
                <div
                  style={{
                    width: 80,
                    height: 2,
                    background: "linear-gradient(to right, #1a365d, transparent)",
                    borderRadius: 1,
                    marginTop: 4,
                  }}
                />
              </div>
            )}

            {/* The message — handwritten style */}
            {config.message && (
              <div className="relative z-10 my-6">
                <Typography.Paragraph
                  style={{
                    fontFamily: "'Caveat', cursive",
                    fontSize: "1.25rem",
                    lineHeight: 2,
                    color: "#1a365d",
                    marginBottom: 0,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {config.message}
                </Typography.Paragraph>
              </div>
            )}

            {/* Sender info — bottom */}
            <div className="relative z-10 mt-8 flex items-end justify-between">
              <div className="flex items-center gap-3">
                {config.sender_avatar && (
                  <Image
                    src={config.sender_avatar}
                    alt="sender"
                    width={48}
                    height={48}
                    preview={false}
                    className="rounded-full object-cover"
                    style={{ border: "2px solid rgba(139,109,63,0.2)" }}
                  />
                )}
                <div>
                  <span
                    style={{
                      fontFamily: "'Courier Prime', monospace",
                      fontSize: "0.6rem",
                      color: "rgba(139,109,63,0.5)",
                      letterSpacing: "0.15em",
                      textTransform: "uppercase",
                    }}
                  >
                    Từ
                  </span>
                  <p
                    style={{
                      fontFamily: "'Caveat', cursive",
                      fontSize: "1.3rem",
                      color: "#2c1810",
                      margin: 0,
                      lineHeight: 1.2,
                    }}
                  >
                    {config.sender_name || "Người gửi"}
                  </p>
                </div>
              </div>

              {/* Wax seal */}
              <div style={{ animation: "pc-float 4s ease-in-out infinite" }}>
                <WaxSeal size={48} />
              </div>
            </div>

            {/* Ink blot decoration */}
            <div
              className="absolute"
              style={{
                bottom: 20,
                left: 16,
                width: 30,
                height: 20,
                borderRadius: "50%",
                background: "rgba(26,54,93,0.06)",
                animation: "pc-ink-blot 5s ease-in-out infinite",
                filter: "blur(3px)",
              }}
            />

            {/* Tape decoration bottom-right */}
            <div
              className="pc-tape absolute"
              style={{
                bottom: -6,
                right: 16,
                width: 50,
                height: 20,
                transform: "rotate(3deg)",
                borderRadius: 2,
                zIndex: 5,
              }}
            />
          </div>
        </AnimatedSection>
      </section>

      <VintageDivider />

      {/* ══════════ VIDEO ══════════ */}
      {config.video && (
        <section className="relative py-10 px-4">
          <AnimatedSection>
            <div className="text-center mb-6">
              <span
                style={{
                  fontFamily: "'Courier Prime', monospace",
                  color: "rgba(139,109,63,0.5)",
                  fontSize: "0.65rem",
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                }}
              >
                Khoảnh khắc
              </span>
              <Typography.Title
                level={4}
                style={{
                  color: "#2c1810",
                  marginTop: 4,
                  fontFamily: "'Playfair Display', serif",
                }}
              >
                Video Kỷ Niệm 🎬
              </Typography.Title>
            </div>

            <div className="pc-paper rounded-lg overflow-hidden max-w-md mx-auto relative" style={{ padding: 8 }}>
              {/* Tape on video */}
              <div
                className="pc-tape absolute z-10"
                style={{
                  top: -4,
                  left: "50%",
                  transform: "translateX(-50%) rotate(-1deg)",
                  width: 70,
                  height: 22,
                  borderRadius: 2,
                }}
              />
              <video
                src={config.video}
                controls
                playsInline
                className="w-full rounded"
                style={{ aspectRatio: "16/9", objectFit: "cover" }}
                poster={config.cover_image}
              />
            </div>
          </AnimatedSection>
        </section>
      )}

      {/* ══════════ GALLERY ══════════ */}
      {config.gallery && config.gallery.length > 0 && (
        <section className="relative py-12 px-4">
          <VintageDivider />

          <AnimatedSection className="text-center mb-8">
            <span
              style={{
                fontFamily: "'Courier Prime', monospace",
                color: "rgba(139,109,63,0.5)",
                fontSize: "0.65rem",
                letterSpacing: "0.2em",
                textTransform: "uppercase",
              }}
            >
              Bộ sưu tập
            </span>
            <Typography.Title
              level={3}
              style={{ color: "#2c1810", marginTop: 4, fontFamily: "'Playfair Display', serif" }}
            >
              Những Kỷ Niệm Đẹp
            </Typography.Title>
          </AnimatedSection>

          {/* Photo stack / scattered polaroid style */}
          <div className="max-w-md mx-auto columns-2 gap-4" style={{ columnGap: 14 }}>
            {config.gallery.map((img, i) => {
              const rotations = [-2, 1.5, -1, 2, -1.5, 0.8, -0.5, 1.8];
              const rot = rotations[i % rotations.length];

              return (
                <AnimatedSection key={i} delay={i * 130} className="mb-4 break-inside-avoid">
                  <div
                    className="pc-gallery-img pc-paper relative"
                    style={{
                      padding: "8px 8px 32px 8px",
                      borderRadius: 4,
                      transform: `rotate(${rot}deg)`,
                    }}
                  >
                    {/* Tape on top */}
                    {i % 3 === 0 && (
                      <div
                        className="pc-tape absolute z-10"
                        style={{
                          top: -5,
                          left: "30%",
                          width: 45,
                          height: 18,
                          transform: `rotate(${rot > 0 ? -3 : 3}deg)`,
                          borderRadius: 1,
                        }}
                      />
                    )}
                    <Image
                      src={img}
                      alt={`memory-${i}`}
                      className="w-full object-cover"
                      style={{
                        aspectRatio: i % 3 === 0 ? "3/4" : i % 3 === 1 ? "1/1" : "4/5",
                        display: "block",
                        borderRadius: 2,
                      }}
                    />
                    {/* Caption area */}
                    <p
                      className="text-center mt-2"
                      style={{
                        fontFamily: "'Caveat', cursive",
                        fontSize: "0.8rem",
                        color: "rgba(44,24,16,0.35)",
                      }}
                    >
                      ♡
                    </p>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        </section>
      )}

      {/* ══════════ CLOSING ══════════ */}
      <section className="relative py-16 px-4">
        <AnimatedSection className="text-center">
          <div className="mb-6" style={{ animation: "pc-float 4s ease-in-out infinite" }}>
            <WaxSeal size={64} />
          </div>

          <h3
            style={{
              fontFamily: "'Caveat', cursive",
              fontSize: "clamp(2rem, 8vw, 2.8rem)",
              color: "#2c1810",
              marginBottom: 12,
            }}
          >
            Gửi Trọn Yêu Thương 💌
          </h3>

          <p
            className="max-w-xs mx-auto"
            style={{
              fontFamily: "'Lora', serif",
              fontSize: "0.95rem",
              color: "rgba(44,24,16,0.4)",
              fontStyle: "italic",
              lineHeight: 1.8,
            }}
          >
            Mỗi bưu thiếp là một mảnh ghép nhỏ của kỷ niệm, chở theo yêu thương và nhớ nhung...
          </p>

          {/* Decorative postmark */}
          <div className="mt-10 flex items-center justify-center">
            <div
              className="flex items-center justify-center"
              style={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                border: "2px solid rgba(139,109,63,0.15)",
                opacity: 0.5,
              }}
            >
              <div className="text-center">
                <div
                  style={{
                    fontFamily: "'Courier Prime', monospace",
                    fontSize: "0.5rem",
                    color: "#8b6d3f",
                    letterSpacing: "0.1em",
                  }}
                >
                  SENT WITH
                </div>
                <div style={{ fontSize: "1rem" }}>❤️</div>
                <div
                  style={{
                    fontFamily: "'Courier Prime', monospace",
                    fontSize: "0.5rem",
                    color: "#8b6d3f",
                  }}
                >
                  2025
                </div>
              </div>
            </div>
          </div>
        </AnimatedSection>
      </section>

      {/* ══════════ FOOTER ══════════ */}
      <div className="w-full">
        <AirmailStrip />
      </div>
      <footer className="relative py-6 px-4 text-center" style={{ background: "#f0e9dc" }}>
        <Typography.Text
          style={{
            fontFamily: "'Courier Prime', monospace",
            color: "rgba(139,109,63,0.3)",
            fontSize: "0.7rem",
            letterSpacing: "0.05em",
          }}
        >
          Made with ❤️ • Powered by Miraway
        </Typography.Text>
      </footer>
    </div>
  );
};

export default PublicPostcard;
